import styled from 'styled-components';

type Metric = {
  label: string;
  levelText: string;
  color: string;
  width: number;
};
type AnalysisCard = {
  title: string;
  countLabel: string;
  grade: { text: string; color: string };
  metrics: Metric[];
};

const TOTAL = 2242;
const WIN = 1932;
const LOSE = 310;

const CARDS: AnalysisCard[] = [
  {
    title: '전체물건',
    countLabel: `경매건수 ${TOTAL.toLocaleString()}건`,
    grade: { text: '우수', color: '#27A131' },
    metrics: [
      { label: '안전성', levelText: '매우높음', color: '#008AFF', width: 250 },
      {
        label: '시세차익가능성',
        levelText: '보통',
        color: '#27A131',
        width: 237,
      },
      { label: '수익성', levelText: '보통', color: '#27A131', width: 178 },
    ],
  },
  {
    title: '수익(Win)물건',
    countLabel: `경매건수 ${WIN.toLocaleString()}건`,
    grade: { text: '우수', color: '#27A131' },
    metrics: [
      { label: '안전성', levelText: '매우높음', color: '#008AFF', width: 250 },
      {
        label: '시세차익가능성',
        levelText: '보통',
        color: '#27A131',
        width: 237,
      },
      { label: '수익성', levelText: '보통', color: '#27A131', width: 178 },
    ],
  },
  {
    title: '손실(Lose)물건',
    countLabel: `경매건수 ${LOSE.toLocaleString()}건`,
    grade: { text: '요주의', color: '#EC0010' },
    metrics: [
      { label: '안전성', levelText: '매우낮음', color: '#EC0010', width: 72 },
      {
        label: '시세차익가능성',
        levelText: '낮음',
        color: '#EC5717',
        width: 129,
      },
      { label: '수익성', levelText: '낮음', color: '#EC5717', width: 119 },
    ],
  },
];

function TotalAnalysis() {
  return (
    <Container>
      <Header>
        <Title>종합분석</Title>
        <Desc>
          과거 수십년간의 경매 낙찰, 낙찰 후 매각 데이터를 분석해 수익과 손실을
          구분하고 각 물건의 안전성, 시세차익가능성, 수익성을 분석한 결과입니다.
        </Desc>
      </Header>

      <Body>
        <GraphArea>
          <PieChart>
            <img
              src="/assets/images/guide-landing/algo/total-pie-chart.svg"
              alt="pie chart"
            />
            <PieValue>
              <Label>전체물건</Label>
              <span>{TOTAL.toLocaleString()}</span>
            </PieValue>
          </PieChart>
          <Legend>
            <LegendItem>
              <LegendColor $bg="#008AFF" />
              <Label>수익물건</Label>
              <TotalSectionName>{WIN.toLocaleString()}</TotalSectionName>
            </LegendItem>
            <LegendItem>
              <LegendColor $bg="#F45551" />
              <Label>손실물건</Label>
              <TotalSectionName>{LOSE.toLocaleString()}</TotalSectionName>
            </LegendItem>
          </Legend>
        </GraphArea>

        <Cards>
          {CARDS.map((c) => (
            <Analysis key={c.title}>
              <AnalysisTitleRow>
                <TitleRowLeft>
                  <TitleText>{c.title}</TitleText>
                  <Grade $color={c.grade.color}>{c.grade.text}</Grade>
                </TitleRowLeft>
                <Count>{c.countLabel}</Count>
              </AnalysisTitleRow>

              <Panel>
                {c.metrics.map((m) => (
                  <MetricBlock key={m.label}>
                    <MetricRow>
                      <Label $color="#222">{m.label}</Label>
                      <Label $color={m.color}>{m.levelText}</Label>
                    </MetricRow>
                    <Bar>
                      <Fill $width={m.width} $color={m.color} />
                    </Bar>
                  </MetricBlock>
                ))}
              </Panel>
            </Analysis>
          ))}
        </Cards>
      </Body>
      <SearchButton>
        <img src="/assets/images/icon/search_white.svg" alt="" />이 알고리즘으로
        물건 검색
      </SearchButton>
    </Container>
  );
}

export default TotalAnalysis;

const Container = styled.div`
  display: flex;
  max-width: 384px;
  padding: 24px 16px;
  flex-direction: column;
  align-items: flex-start;
  gap: 24px;
  align-self: stretch;
`;

const Header = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  gap: 8px;
  align-self: stretch;
`;

const Title = styled.div`
  color: #222;
  font-size: 18px;
  font-weight: 700;
  line-height: 145%;
  letter-spacing: -0.36px;
`;

const Desc = styled.div`
  display: flex;
  padding: 16px;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  align-self: stretch;
  border-radius: 12px;
  background: #f7f8fc;

  color: #616161;
  font-size: 14px;
  font-weight: 500;
  line-height: 145%;
  letter-spacing: -0.28px;
`;

const Body = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 24px;
  align-self: stretch;
`;

const GraphArea = styled.div`
  display: flex;
  flex-direction: row;
  padding: 16px 0;
  justify-content: center;
  align-items: center;
  gap: 24px;
  align-self: stretch;
`;

const PieChart = styled.div`
  width: 160px;
  height: 160px;
  aspect-ratio: 1/1;
  position: relative;
`;

const PieValue = styled.div`
  position: absolute;
  left: 23.5px;
  top: 56px;

  display: flex;
  width: 112px;
  flex-direction: column;
  align-items: center;
  gap: 5px;

  align-self: stretch;
  text-align: center;

  & > span {
    color: #222;
    font-size: 16px;
    font-weight: 500;
    letter-spacing: -0.32px;
  }
`;

const Label = styled.div<{ $color?: string }>`
  color: ${({ $color }) => $color ?? '#616161'};
  font-size: 13px;
  font-weight: 500;
  line-height: 145%;
  letter-spacing: -0.28px;
`;

const Legend = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const LegendItem = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 4px;
`;

const TotalSectionName = styled.div`
  color: #222;
  font-size: 14px;
  font-weight: 500;
  line-height: 145%;
  letter-spacing: -0.28px;
`;

const LegendColor = styled.span<{ $bg: string }>`
  width: 16px;
  height: 16px;
  border-radius: 4px;
  background: ${({ $bg }) => $bg};
`;

const Cards = styled.div`
  width: 100%;
  display: flex;
  align-items: flex-start;
  gap: 24px;
  overflow-x: auto;
  width: calc(100vw - 32px);
  padding-right: 32px;
`;

const Analysis = styled.div`
  display: flex;
  width: 282px;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  flex-shrink: 0;
`;

const AnalysisTitleRow = styled.div`
  display: flex;
  flex-direction: row;
  padding: 0 8px;
  justify-content: space-between;
  align-items: flex-end;
  align-self: stretch;
`;

const TitleRowLeft = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
`;

const TitleText = styled.span`
  color: #222;
  font-size: 14px;
  font-weight: 500;
  line-height: 145%;
  letter-spacing: -0.28px;
`;

const Grade = styled.div<{ $color?: string }>`
  color: ${({ $color }) => $color};
  font-size: 14px;
  font-weight: 700;
  line-height: 145%;
  letter-spacing: -0.28px;
`;

const Count = styled.div`
  color: #616161;
  font-size: 12px;
  font-weight: 500;
  line-height: 145%;
  letter-spacing: -0.24px;
`;

const Panel = styled.div`
  display: flex;
  padding: 16px;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
  align-self: stretch;
  border-radius: 8px;
  border: 1px solid #e5e8ea;
  background: #fafafb;
`;

const MetricBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  align-self: stretch;
`;

const MetricRow = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  align-self: stretch;
`;

const Bar = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  align-self: stretch;
  border-radius: 999px;
  background: rgba(112, 115, 124, 0.08);
`;

const Fill = styled.div<{
  $width: number;
  $color: string;
}>`
  width: ${({ $width }) => `${$width}px`};
  height: 4px;
  border-radius: 4px;
  background: ${({ $color }) => $color};
`;

const SearchButton = styled.div`
  padding: 12px 16px;
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  gap: 4px;
  align-self: stretch;
  border-radius: 8px;
  background: #008aff;

  color: #fff;
  text-align: center;
  font-size: 16px;
  font-weight: 500;
  line-height: 145%;
  letter-spacing: -0.32px;
`;
